/**
 * In-memory handoff that carries a parent's photo, people, idea and language
 * across a picture-book <-> comic switch.
 *
 * A client navigation keeps this module alive; a full reload loses it, and the
 * flow then starts fresh, which is the honest re-upload case. Reading is a
 * peek: the receiving page clears it in an effect, so a strict-mode remount can
 * still read it.
 */
import type { MangaPhoto, MangaCharacterDraft } from '@/components/manga/types';
import { isOfferedLanguage, isComicLanguage } from '@/utils/languages';
import type { CreateFlow } from '@/utils/analytics-events';
import { otherFormat } from './format-copy';

export interface FormatSnapshot {
  photo: MangaPhoto | null;
  characters: MangaCharacterDraft[];
  title: string;
  idea: string;
  language: string;
}

export interface FormatHandoff extends FormatSnapshot {
  from: CreateFlow;
  to: CreateFlow;
  at: number;
}

export const HANDOFF_TTL_MS = 600_000;

const PICTURE_BOOK_IDEA_MAX = 500;

let current: FormatHandoff | null = null;

export function buildHandoff(from: CreateFlow, s: FormatSnapshot, now: number = Date.now()): FormatHandoff {
  const to = otherFormat(from);
  // A photo is carried only with its own consent tick. A cover-seeded photo
  // has none, and the other flow would send it without consent.
  const photo = s.photo?.photoConsentVersion ? s.photo : null;
  const langOk = (to === 'comic' ? isComicLanguage : isOfferedLanguage)(s.language);
  return {
    from,
    to,
    at: now,
    photo,
    characters: photo ? s.characters : [],
    title: s.title,
    idea: to === 'picture-book' ? s.idea.slice(0, PICTURE_BOOK_IDEA_MAX) : s.idea,
    language: langOk ? s.language : 'en',
  };
}

export function setFormatHandoff(h: FormatHandoff): void {
  current = h;
}

export function peekFormatHandoff(to: CreateFlow, now: number = Date.now()): FormatHandoff | null {
  if (!current || current.to !== to) return null;
  if (now - current.at > HANDOFF_TTL_MS) return null;
  return current;
}

export function clearFormatHandoff(): void {
  current = null;
}

export function formatHref(to: CreateFlow): '/create-book' | '/create-comic' {
  return to === 'comic' ? '/create-comic' : '/create-book';
}

export function handoffVariant(h: FormatHandoff): 'carried' | 'reupload' {
  return h.photo ? 'carried' : 'reupload';
}

export function handoffNote(h: FormatHandoff): string | null {
  const hasIdea = Boolean(h.idea.trim() || h.title.trim());
  if (h.photo) {
    const people = h.characters.length > 0;
    const kept = people ? 'photo, people' : 'photo';
    return hasIdea
      ? `We kept your ${kept} and idea.`
      : people
        ? 'We kept your photo and people.'
        : 'We kept your photo.';
  }
  return hasIdea ? 'We kept your idea. Please add your photo again.' : 'Please add your photo again.';
}
